/**
 * URL Handlers
 */

import { useCallback, useMemo } from "react"
import { toast } from "sonner"
import { openSidebarMode } from "@/lib/sidebar-mode"
import { nextcloudFieldsFromTab } from "@/lib/nextcloud-integration-nav"
import { useWorkspace } from "@/providers/workspace-provider"
import {
  markFailedDocumentLoad,
  markProgrammaticNavigation,
  type UrlChangeParams,
} from "@/hooks/use-url-sync"
import {
  workspaceIdForSlug,
  workspaceIdForDocumentUrlPath,
  normalizeFolderStoragePath,
  resolveStoragePathForFetch,
  findOpenTabForDocumentPath,
  displayFolderTitleForStoragePath,
  documentUrlPathsEquivalent,
  isUuid,
} from "@/lib/app-routes"
import {
  clearDocumentDeepLinkPending,
  documentOpenMissDisposition,
  getPendingDocumentDeepLink,
  isDocumentDeepLinkRestorePending,
  pendingDeepLinkMatchesContentId,
} from "@/lib/document-deep-link-pending"
import type { OpenTab } from "../../features/document-tabs"
import { resolveNavigationTarget } from "@/lib/api"
import { fetchDocumentData, generateTabSlotId } from "../resizable-layout.utils"
import type { DocumentData } from "../resizable-layout.types"
import type { usePerTabNavigationHistory } from "@/hooks/use-navigation-history"
import { DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH } from "@/lib/product-board-default-doc"
import { pinHomeTabFirstInOpenTabs, resolveTabWorkspaceId } from "./shared"
import {
  clearUserHiddenWorkspace,
  isWorkspaceAutoEnableBlocked,
  openingPathnameForDocument,
} from "@/lib/user-hidden-workspaces"
import {
  navigationFieldsFromIntegrationOpen,
  navigationFieldsFromOpenTab,
} from "@/lib/basecamp-integration-nav"
import { consumeNextUrlOpenIntent } from "@/lib/open-intent"
import {
  PEOPLE_INDEX_TAB_ID,
  drillOpenTabToPerson,
  personViewTabTitle,
  openPeopleIndexInTabs,
  resolvePersonWorkspaceId,
} from "@/lib/person-view"

/**
 * Open a Basecamp integration tab from URL sync.
 * Prefer replacing the active unpinned tab (preserves slot history for Back)
 * over appending a fresh slot (which left Back disabled after list opens).
 */
function openBasecampTabFromUrl(args: {
  openTabs: OpenTab[]
  activeTabId: string | null
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void
  setActiveTabId: (id: string | null) => void
  navigation: ReturnType<typeof usePerTabNavigationHistory>
  newTab: OpenTab
  caller: string
}) {
  const {
    openTabs,
    activeTabId,
    setOpenTabs,
    setActiveTabId,
    navigation,
    newTab,
    caller,
  } = args
  const currentTab = activeTabId
    ? openTabs.find((tab) => tab.id === activeTabId)
    : undefined
  const forcedIntent = consumeNextUrlOpenIntent()
  const canReplace =
    forcedIntent !== 'new' &&
    !!currentTab &&
    !currentTab.isPinned &&
    currentTab.type !== 'home' &&
    currentTab.id !== newTab.id

  if (canReplace && currentTab) {
    const slotId = currentTab.tabSlotId || generateTabSlotId()
    const tip = navigation.getCurrentEntry(slotId)
    if (!tip || tip.documentId !== currentTab.id) {
      navigation.navigate(
        slotId,
        navigationFieldsFromOpenTab(currentTab),
        `${caller}-seed`,
      )
    }
    const placed: OpenTab = { ...newTab, tabSlotId: slotId }
    setOpenTabs(
      openTabs.map((tab) => (tab.id === activeTabId ? placed : tab)),
    )
    setActiveTabId(newTab.id)
    navigation.navigate(
      slotId,
      navigationFieldsFromIntegrationOpen({
        tabId: newTab.id,
        type: newTab.type,
        integrationData: newTab.integrationData,
      }),
      caller,
    )
    return
  }

  const slotId = newTab.tabSlotId || generateTabSlotId()
  const placed: OpenTab = { ...newTab, tabSlotId: slotId }
  setOpenTabs([...openTabs, placed])
  setActiveTabId(newTab.id)
  navigation.navigate(
    slotId,
    navigationFieldsFromIntegrationOpen({
      tabId: newTab.id,
      type: newTab.type,
      integrationData: newTab.integrationData,
    }),
    caller,
  )
}

export function useUrlHandlers(
  openTabs: OpenTab[],
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
  setSelectedProjectId: (id: string | null) => void,
  navigation: ReturnType<typeof usePerTabNavigationHistory>,
  createNewTab: (
    documentId: string,
    prefetched?: DocumentData | null,
    options?: { reuseTabSlotId?: string; fromUrlSync?: boolean },
  ) => Promise<void>,
  switchToExistingTab: (
    documentId: string,
    existingTab: OpenTab,
    options?: { fromUrlSync?: boolean },
  ) => Promise<void>,
  getWorkspaceIdForPath?: (path: string) => string | undefined
) {
  const { workspaces, activeWorkspaceIds, setActiveWorkspaceIds, hideOtherWorkspaces } =
    useWorkspace()
  /** Match document-viewer integration tabs: prefer an enabled "work" org (Basecamp lives there). */
  const defaultIntegrationWorkspaceId = useMemo(() => {
    const firstActiveWork = activeWorkspaceIds
      .map((id) => workspaces.find((w) => w.id === id))
      .find((w) => w && w.type === "work")
    return firstActiveWork?.id
  }, [workspaces, activeWorkspaceIds])

  const resolveExplorerWs = (folderPath: string | null | undefined): string | undefined => {
    if (!folderPath || !getWorkspaceIdForPath) return undefined
    return getWorkspaceIdForPath(folderPath)
  }

  const workspacesRoute = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        slug: w.slug,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaces]
  )

  const openExplorerAtFolder = useCallback(
    (folderPath: string | null | undefined) => {
      const storageFolder =
        normalizeFolderStoragePath(folderPath, workspacesRoute) ?? folderPath ?? null
      const wsId = resolveExplorerWs(storageFolder)
      const folderTitle = storageFolder
        ? displayFolderTitleForStoragePath(storageFolder, workspacesRoute)
        : 'Explorer'
      const PATH_UUID_RE =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      const isProvisionalPathNote = (tab: OpenTab) =>
        tab.type === 'note' &&
        !PATH_UUID_RE.test(tab.id) &&
        (tab.id.includes('/') || Boolean(tab.filePath?.includes('/')))

      // Functional update: convert/remove path-keyed provisional note tabs so URL sync
      // cannot re-open the folder path as a stuck "Loading document..." note tab.
      let navSlot = 'tab-main'
      let activeId = 'list'
      setOpenTabs((prev) => {
        const explorerTab = prev.find((tab) => tab.type === 'explorer')
        const listTab = prev.find((tab) => tab.id === 'list')
        const provisional = prev.find(isProvisionalPathNote)
        const withoutProvisional = prev.filter((tab) => !isProvisionalPathNote(tab))

        if (provisional && withoutProvisional.every((t) => t.type !== 'explorer')) {
          navSlot = provisional.tabSlotId || 'tab-main'
          activeId = 'list'
          return [
            {
              id: 'list',
              title: folderTitle,
              type: 'explorer' as const,
              isDirty: false,
              isPinned: false,
              tabSlotId: navSlot,
              explorerData: { folderPath: storageFolder },
              ...(wsId != null && { workspaceId: wsId }),
            },
            ...withoutProvisional.filter((t) => t.id !== 'list'),
          ]
        }

        const target = explorerTab ?? listTab
        if (target) {
          activeId = target.id
          navSlot = target.tabSlotId || 'tab-main'
          return withoutProvisional.map((tab) =>
            tab.id === target.id
              ? {
                  ...tab,
                  title: folderTitle,
                  explorerData: { folderPath: storageFolder },
                  ...(wsId != null && { workspaceId: wsId }),
                }
              : tab,
          )
        }

        activeId = 'list'
        navSlot = 'tab-main'
        return [
          ...withoutProvisional,
          {
            id: 'list',
            title: folderTitle,
            type: 'explorer' as const,
            isDirty: false,
            isPinned: false,
            tabSlotId: 'tab-main',
            explorerData: { folderPath: storageFolder },
            ...(wsId != null && { workspaceId: wsId }),
          },
        ]
      })
      setActiveTabId(activeId)
      setSelectedProjectId(storageFolder)
      markProgrammaticNavigation()
      navigation.navigate(
        navSlot,
        {
          documentId: null,
          folderPath: storageFolder,
          viewMode: 'list',
        },
        'url-explorer-folder',
      )
    },
    [
      setOpenTabs,
      setActiveTabId,
      setSelectedProjectId,
      navigation,
      getWorkspaceIdForPath,
      workspacesRoute,
    ]
  )

  /** URL restoration may specify organization for Basecamp tabs. */
  const effectiveOrganizationId = (params: UrlChangeParams): string | undefined => {
    if (params.workspaceId) return params.workspaceId
    if (params.workspaceSlug) {
      const fromSlug = workspaceIdForSlug(workspaces, params.workspaceSlug)
      if (fromSlug) return fromSlug
    }
    return defaultIntegrationWorkspaceId
  }

  /**
   * URL intent (paste, bookmark, address bar): enable the doc workspace before by-path.
   * Returns false when workspace cannot be resolved (cold deep link should fail closed).
   */
  const ensureWorkspaceEnabledForUrlDocument = useCallback(
    async (contentId: string): Promise<boolean> => {
      // Do not bail when activeWorkspaceIds is empty - that is exactly when deep links get stuck
      // (sidebar shows WORKSPACES (0) and the document never resolves).
      if (workspaces.length === 0) {
        return false
      }

      let docPath: string | null = contentId.includes('/') ? contentId : null
      if (!docPath) {
        try {
          const data = await fetchDocumentData(contentId, {
            workspaces: workspacesRoute,
          })
          docPath = data?.filePath ?? data?.path ?? null
        } catch {
          return false
        }
      }
      if (!docPath) return false

      // Slug / display-name -> storage before workspace resolve (never require by-path first).
      const storagePath = resolveStoragePathForFetch(docPath, workspacesRoute)
      const wsId =
        workspaceIdForDocumentUrlPath(docPath, workspacesRoute) ??
        workspaceIdForDocumentUrlPath(storagePath, workspacesRoute) ??
        getWorkspaceIdForPath?.(storagePath) ??
        getWorkspaceIdForPath?.(docPath)

      if (!wsId) {
        if (isDocumentDeepLinkRestorePending()) {
          toast.error('Could not open document', {
            description: 'Workspace for this link could not be resolved.',
          })
          clearDocumentDeepLinkPending()
        }
        return false
      }

      // User hid this workspace on this exact URL. Do not add it back.
      // Cold paste / new Chrome tab still enables (deep-link pending).
      if (
        isWorkspaceAutoEnableBlocked(wsId, openingPathnameForDocument(contentId)) &&
        !isDocumentDeepLinkRestorePending()
      ) {
        return false
      }
      if (isDocumentDeepLinkRestorePending()) {
        clearUserHiddenWorkspace(wsId)
      }

      // Cold Chrome-tab deep link: focus only this workspace (product rule) before fetch.
      if (isDocumentDeepLinkRestorePending()) {
        if (activeWorkspaceIds.length !== 1 || activeWorkspaceIds[0] !== wsId) {
          hideOtherWorkspaces(wsId)
        }
        return true
      }

      if (activeWorkspaceIds.includes(wsId)) return true

      const previousIds = activeWorkspaceIds
      const workspace = workspaces.find((w) => w.id === wsId)
      const name = workspace?.name ?? 'workspace'

      // Empty active set: enable just this workspace (no toast noise).
      if (previousIds.length === 0) {
        setActiveWorkspaceIds([wsId])
        return true
      }

      setActiveWorkspaceIds([...previousIds, wsId])
      toast(`Enabled ${name} to open this document`, {
        action: {
          label: 'Undo',
          onClick: () => setActiveWorkspaceIds(previousIds),
        },
      })
      return true
    },
    [
      getWorkspaceIdForPath,
      workspaces,
      workspacesRoute,
      activeWorkspaceIds,
      setActiveWorkspaceIds,
      hideOtherWorkspaces,
    ],
  )

  /** Apply organization + workspace grouping from URL restoration. */
  const patchTabOrganizationFromParams = (
    tab: OpenTab,
    orgId?: string | null,
  ): OpenTab => {
    if (!orgId) return tab
    const bc =
      tab.type === 'integration-basecamp' ||
      tab.type === 'integration-basecamp-todo' ||
      tab.type === 'integration-basecamp-todo-list' ||
      tab.type === 'integration-basecamp-triage'
    if (!bc) return { ...tab, workspaceId: orgId }
    return {
      ...tab,
      workspaceId: orgId,
      ...(tab.integrationData
        ? {
            integrationData: {
              ...tab.integrationData,
              organizationId: orgId,
            },
          }
        : {}),
    }
  }

  /**
   * Handle URL change (from browser navigation, bookmarks, etc.)
   */
  const handleUrlChange = useCallback(async (contentId: string, params: UrlChangeParams) => {
    if (params.type === 'home') {
      const homeTabId = 'home'
      const existingHomeTab = openTabs.find((tab) => tab.id === homeTabId || tab.type === 'home')
      markProgrammaticNavigation()
      if (existingHomeTab) {
        setOpenTabs(pinHomeTabFirstInOpenTabs(openTabs))
        setActiveTabId(homeTabId)
        const slotId = existingHomeTab.tabSlotId || 'tab-main'
        navigation.navigate(slotId, { documentId: null, viewMode: 'list' }, 'url-home-existing')
        return
      }
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      const stableTabSlotId = currentTab?.tabSlotId || 'tab-main'
      const homeTab: OpenTab = {
        id: homeTabId,
        title: 'Home',
        type: 'home',
        isDirty: false,
        tabSlotId: stableTabSlotId,
      }
      if (currentTab && !currentTab.isPinned) {
        setOpenTabs(openTabs.map((tab) => (tab.id === activeTabId ? homeTab : tab)))
      } else {
        setOpenTabs([...openTabs, homeTab])
      }
      setActiveTabId(homeTabId)
      navigation.navigate(stableTabSlotId, { documentId: null, viewMode: 'list' }, 'url-home-replace')
      return
    }

    // Legacy Activity tab type / URL — open Search Activity named view instead
    if (params.type === 'edit-history' || params.type === 'search') {
      const searchTabId = 'search-view'
      const namedView: 'search' | 'activity' =
        params.type === 'edit-history' || params.searchNamedView === 'activity'
          ? 'activity'
          : 'search'
      const searchQuery = params.searchQuery ?? ''
      const searchPath = params.searchPath?.trim() || undefined
      const existing = openTabs.find((tab) => tab.id === searchTabId || tab.type === 'search-view')
      markProgrammaticNavigation()
      if (existing) {
        setOpenTabs((prev) =>
          prev.map((tab) =>
            tab.id === searchTabId || tab.type === 'search-view'
              ? {
                  ...tab,
                  id: searchTabId,
                  type: 'search-view' as const,
                  searchNamedView: namedView,
                  title: namedView === 'activity' ? 'Activity' : 'Search',
                  ...(namedView === 'search'
                    ? {
                        initialQuery: searchQuery,
                        searchPath,
                      }
                    : {}),
                  ...(params.searchUpdatedBy
                    ? { searchUpdatedBy: params.searchUpdatedBy }
                    : {}),
                  searchSemantic: Boolean(params.searchSemantic),
                }
              : tab,
          ),
        )
        setActiveTabId(searchTabId)
        const slotId = existing.tabSlotId || 'tab-main'
        navigation.navigate(slotId, { documentId: searchTabId, viewMode: 'single' }, 'url-search-existing')
        return
      }
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      const stableTabSlotId = currentTab?.tabSlotId || 'tab-main'
      const searchTab: OpenTab = {
        id: searchTabId,
        title: namedView === 'activity' ? 'Activity' : 'Search',
        type: 'search-view',
        isDirty: false,
        tabSlotId: stableTabSlotId,
        searchNamedView: namedView,
        ...(namedView === 'search'
          ? {
              initialQuery: searchQuery,
              searchPath,
            }
          : {}),
        ...(params.searchUpdatedBy ? { searchUpdatedBy: params.searchUpdatedBy } : {}),
        searchSemantic: Boolean(params.searchSemantic),
      }
      setOpenTabs([...openTabs, searchTab])
      setActiveTabId(searchTabId)
      navigation.navigate(stableTabSlotId, { documentId: searchTabId, viewMode: 'single' }, 'url-search-new')
      return
    }

    if (params.type === 'people-index') {
      const peopleTabId = PEOPLE_INDEX_TAB_ID
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      const fromUrl =
        params.workspaceId
        ?? (params.workspaceSlug ? workspaceIdForSlug(workspaces, params.workspaceSlug) : undefined)
      const peopleWorkspaceId = resolvePersonWorkspaceId(
        fromUrl,
        currentTab?.workspaceId,
        activeWorkspaceIds,
      )
      markProgrammaticNavigation()
      const peopleTab: OpenTab = {
        id: peopleTabId,
        title: 'People',
        type: 'person-view',
        isDirty: false,
        tabSlotId: currentTab?.tabSlotId || 'tab-main',
        workspaceId: peopleWorkspaceId,
      }
      const placed = openPeopleIndexInTabs(openTabs, activeTabId, peopleTab)
      if (placed.replacedTabId && currentTab) {
        const tip = navigation.getCurrentEntry(placed.slotId)
        if (!tip || tip.documentId !== currentTab.id) {
          navigation.navigate(
            placed.slotId,
            navigationFieldsFromOpenTab(currentTab),
            'url-people-seed',
          )
        }
      }
      setOpenTabs(placed.tabs)
      setActiveTabId(peopleTabId)
      navigation.navigate(
        placed.slotId,
        {
          documentId: peopleTabId,
          viewMode: 'single',
          tabType: 'person-view',
          title: 'People',
          ...(peopleWorkspaceId ? { organizationId: peopleWorkspaceId } : {}),
        },
        'url-people',
      )
      return
    }

    if (params.type === 'person' && params.personUserId) {
      const personUserId = params.personUserId
      const personTabId = `person-view-${personUserId}`
      const personTitle = personViewTabTitle('Person')
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      const fromUrl =
        params.workspaceId
        ?? (params.workspaceSlug ? workspaceIdForSlug(workspaces, params.workspaceSlug) : undefined)
      const personWorkspaceId = resolvePersonWorkspaceId(
        fromUrl,
        currentTab?.workspaceId,
        activeWorkspaceIds,
      )
      markProgrammaticNavigation()
      let slotId = 'tab-main'
      setOpenTabs((prev) => {
        const current = prev.find((tab) => tab.id === activeTabId)
        if (current?.type === 'person-view' || prev.some((tab) => tab.id === PEOPLE_INDEX_TAB_ID || tab.type === 'person-view')) {
          const drilled = drillOpenTabToPerson(prev, current?.id ?? activeTabId, {
            tabId: personTabId,
            userId: personUserId,
            title: personTitle,
          })
          const next = drilled.find((tab) => tab.id === personTabId)
          slotId = next?.tabSlotId || current?.tabSlotId || 'tab-main'
          if (next) {
            return drilled.map((tab) =>
              tab.id === personTabId
                ? { ...tab, workspaceId: personWorkspaceId ?? tab.workspaceId }
                : tab,
            )
          }
        }
        const existingPerson = prev.find(
          (tab) => tab.id === personTabId || tab.personUserId === personUserId,
        )
        if (existingPerson) {
          slotId = existingPerson.tabSlotId || 'tab-main'
          return prev.map((tab) =>
            tab.id === existingPerson.id
              ? { ...tab, workspaceId: personWorkspaceId ?? tab.workspaceId }
              : tab,
          )
        }
        slotId = current?.tabSlotId || 'tab-main'
        return [
          ...prev,
          {
            id: personTabId,
            title: personTitle,
            type: 'person-view' as const,
            isDirty: false,
            tabSlotId: slotId,
            personUserId,
            workspaceId: personWorkspaceId,
          },
        ]
      })
      setActiveTabId(personTabId)
      navigation.navigate(
        slotId,
        {
          documentId: personTabId,
          viewMode: 'single',
          tabType: 'person-view',
          title: personTitle,
          ...(personWorkspaceId ? { organizationId: personWorkspaceId } : {}),
        },
        'url-person',
      )
      return
    }

    // Handle Explorer type
    if (params.type === 'explorer') {
      openExplorerAtFolder(params.folderPath ?? null)
      return;
    }

    // Workspace dashboard from URL (/workspace/:id)
    if (params.type === "workspace-home") {
      const wsId =
        (params.workspaceId ?? contentId.replace(/^workspace-home-/, "")) || ""
      const tabId = `workspace-home-${wsId}`
      const whTab = openTabs.find((tab) => tab.id === tabId)
      if (whTab) {
        await switchToExistingTab(tabId, whTab)
      } else if (wsId) {
        window.dispatchEvent(
          new CustomEvent("open-workspace-home", {
            detail: { workspaceId: wsId },
          })
        )
      }
      return
    }

    // Check if tab already exists (may merge org from scoped Basecamp URLs)
    const orgFromParams = params.workspaceId
    let existingTab =
      findOpenTabForDocumentPath(openTabs, contentId, workspacesRoute) ??
      openTabs.find((tab) => tab.id === contentId)
    const needsOrgPatch =
      !!existingTab &&
      !!orgFromParams &&
      (existingTab.workspaceId !== orgFromParams ||
        existingTab.integrationData?.organizationId !== orgFromParams)
    if (needsOrgPatch && existingTab && orgFromParams) {
      const merged = patchTabOrganizationFromParams(existingTab, orgFromParams)
      setOpenTabs((prev) =>
        prev.map((tab) => (tab.id === contentId ? merged : tab))
      )
      existingTab = merged
    }

    // Cold deep-link provisional tabs use the path as id (not a UUID). Treating them as
    // "already open" skips createNewTab and leaves folder URLs stuck on Loading document.
    const existingIsProvisionalPathNote =
      !!existingTab &&
      params.type === 'document' &&
      contentId.includes('/') &&
      !isUuid(existingTab.id) &&
      existingTab.type === 'note'

    if (existingTab && !existingIsProvisionalPathNote) {
      if (params.type === 'document') {
        const wsGuess =
          (contentId.includes('/')
            ? workspaceIdForDocumentUrlPath(contentId, workspacesRoute)
            : undefined) ?? resolveTabWorkspaceId(existingTab, workspaces)
        if (
          wsGuess &&
          isWorkspaceAutoEnableBlocked(wsGuess, openingPathnameForDocument(contentId)) &&
          !isDocumentDeepLinkRestorePending()
        ) {
          return
        }
        await ensureWorkspaceEnabledForUrlDocument(contentId)
      }
      await switchToExistingTab(existingTab.id, existingTab, { fromUrlSync: true })
      return
    }

    try {
      // Need to create new content based on type
        if (params.type === 'document') {
          const wasColdPending = isDocumentDeepLinkRestorePending()
          if (contentId.includes('/') && !wasColdPending) {
            const wsGuess = workspaceIdForDocumentUrlPath(contentId, workspacesRoute)
            if (
              wsGuess &&
              isWorkspaceAutoEnableBlocked(wsGuess, openingPathnameForDocument(contentId))
            ) {
              return
            }
          }
          const workspaceReady =
            await ensureWorkspaceEnabledForUrlDocument(contentId)
          if (wasColdPending && !workspaceReady) {
            // Workspaces are still loading. Keep the provisional document tab.
            // Do not paint a folder; url-sync retries once the org list is in.
            return
          }
          const storagePath = resolveStoragePathForFetch(contentId, workspacesRoute)
          const leaf = storagePath.split('/').pop() || ''
          const extensionless =
            contentId.includes('/') && !/\.(md|html|csv)$/i.test(leaf)
          try {
            if (extensionless) {
              // Decide document vs folder before either view paints (ADR-0012).
              const resolved = await resolveNavigationTarget(storagePath)
              if (resolved.kind === 'document') {
                const prefetched: DocumentData = {
                  id: resolved.id,
                  title: resolved.title,
                  type: resolved.type as DocumentData['type'],
                  filePath: resolved.filePath,
                  organizationId: resolved.organizationId,
                }
                await createNewTab(contentId, prefetched, { fromUrlSync: true })
                if (wasColdPending) clearDocumentDeepLinkPending()
                return
              }
              if (resolved.kind === 'folder') {
                clearDocumentDeepLinkPending()
                openExplorerAtFolder(resolved.path)
                return
              }
              throw new Error(`Document not found: ${contentId}`)
            }

            // Keep slug/pathOrId as createNewTab input so path-alias upgrade matches the
            // provisional cold-load tab; fetchDocumentData resolves to storage root.
            await createNewTab(contentId, undefined, { fromUrlSync: true });
            if (wasColdPending) {
              clearDocumentDeepLinkPending()
            }
          } catch (error) {
            console.error('[URL Change] Failed to create document tab:', error);
            markFailedDocumentLoad(contentId);
            // Extensionless misses stay off the folder view (that flash was the bug).
            // Paths that already include an extension can still fall through to explorer.
            // A miss for a different path must not cancel the deep link still loading.
            const failureIsPending = pendingDeepLinkMatchesContentId(
              contentId,
              getPendingDocumentDeepLink(),
              (left, right) => documentUrlPathsEquivalent(left, right, workspacesRoute),
            )
            const disposition = documentOpenMissDisposition({
              extensionless,
              pathHasSlash: contentId.includes('/'),
              failureIsPending,
              restorePending: wasColdPending || isDocumentDeepLinkRestorePending(),
            })
            if (disposition === 'ignore') {
              return
            }
            if (disposition === 'explorer') {
              if (failureIsPending) clearDocumentDeepLinkPending()
              const folderPath =
                normalizeFolderStoragePath(contentId, workspacesRoute) ?? contentId
              openExplorerAtFolder(folderPath)
              return
            }
            if (disposition === 'keep-provisional') {
              const label =
                contentId.length > 48 ? `${contentId.slice(0, 45)}...` : contentId
              toast.error('Document not found', {
                description: `Could not open "${label}".`,
              })
              clearDocumentDeepLinkPending()
              // Keep provisional/loading tab - never silent Personal Home on cold paste.
              return
            }
            // Soft-fail: clear bad deep link (e.g. bare slug "changelog") so Home
            // does not sit on a skeleton / retry loop until hard reload.
            const label =
              contentId.length > 48 ? `${contentId.slice(0, 45)}…` : contentId
            toast.error('Document not found', {
              description: `Could not open "${label}". Returning to Home.`,
            })
            markProgrammaticNavigation()
            const existingHome = openTabs.find(
              (tab) => tab.id === 'home' || tab.type === 'home',
            )
            if (existingHome) {
              setOpenTabs(pinHomeTabFirstInOpenTabs(openTabs))
              setActiveTabId('home')
              navigation.navigate(
                existingHome.tabSlotId || 'tab-main',
                { documentId: null, viewMode: 'list' },
                'url-doc-missing',
              )
            } else {
              const homeTab: OpenTab = {
                id: 'home',
                title: 'Home',
                type: 'home',
                isDirty: false,
                tabSlotId: 'tab-main',
              }
              setOpenTabs(pinHomeTabFirstInOpenTabs([...openTabs, homeTab]))
              setActiveTabId('home')
              navigation.navigate(
                'tab-main',
                { documentId: null, viewMode: 'list' },
                'url-doc-missing-new-home',
              )
            }
            try {
              window.history.replaceState({}, '', '/')
            } catch {
              /* ignore */
            }
            return
          }
        } else if (params.type === 'integration-basecamp') {
          const org = effectiveOrganizationId(params)
          const sectionTitles: Record<string, string> = {
            bookmarks: 'Bookmarks', mystuff: 'My Stuff', todos: 'Todos', projects: 'Projects', activity: 'Activity'
          }
          const cachedProjectName = params.projectId
            ? localStorage.getItem(`basecamp-project-name:${params.projectId}`)
            : null
          const newTab: OpenTab = {
            id: contentId,
            title: params.section
              ? sectionTitles[params.section]
              : cachedProjectName ||
                (params.projectId
                  ? `Basecamp Project ${params.projectId}`
                  : 'Basecamp'),
            type: 'integration-basecamp',
            isDirty: false,
            isPinned: false,
            tabSlotId: generateTabSlotId(),
            integrationData: {
              integrationId: 'basecamp',
              projectId: params.projectId || '',
              ...(cachedProjectName ? { projectName: cachedProjectName } : {}),
              ...(params.section ? { section: params.section } : {}),
              ...(params.workspaceSlug ? { workspaceSlug: params.workspaceSlug } : {}),
              ...(org ? { organizationId: org } : {}),
            },
            ...(org != null ? { workspaceId: org } : {}),
          }
          openBasecampTabFromUrl({
            openTabs,
            activeTabId,
            setOpenTabs,
            setActiveTabId,
            navigation,
            newTab,
            caller: 'url-basecamp-section',
          })
        } else if (
          params.type === 'integration-basecamp-todo-list' ||
          (params.type === 'integration-basecamp-todo' && contentId.includes('todo-list'))
        ) {
          const org = effectiveOrganizationId(params)
          const list =
            params.listId ??
            (contentId.includes('todo-list')
              ? contentId.replace('basecamp-todo-list-', '')
              : '')
          const cachedProjectName = params.projectId
            ? localStorage.getItem(`basecamp-project-name:${params.projectId}`)
            : null
          const newTab: OpenTab = {
            id: contentId,
            title: cachedProjectName ? 'Loading list...' : `Todo List ${list || ''}`,
            type: 'integration-basecamp-todo-list',
            isDirty: false,
            tabSlotId: generateTabSlotId(),
            integrationData: {
              integrationId: 'basecamp',
              projectId: params.projectId || '',
              listId: list,
              ...(cachedProjectName ? { projectName: cachedProjectName } : {}),
              ...(params.workspaceSlug ? { workspaceSlug: params.workspaceSlug } : {}),
              ...(org ? { organizationId: org } : {}),
            },
            ...(org != null ? { workspaceId: org } : {}),
          }
          openBasecampTabFromUrl({
            openTabs,
            activeTabId,
            setOpenTabs,
            setActiveTabId,
            navigation,
            newTab,
            caller: 'url-basecamp-todo-list',
          })
        } else if (params.type === 'integration-basecamp-todo') {
          const org = effectiveOrganizationId(params)
          const newTab: OpenTab = {
            id: contentId,
            title: params.todoTitle || `Basecamp Todo ${params.todoId || ''}`,
            type: 'integration-basecamp-todo',
            isDirty: false,
            tabSlotId: generateTabSlotId(),
            integrationData: {
              integrationId: 'basecamp',
              projectId: params.projectId || '',
              todoId: params.todoId || contentId.replace('basecamp-todo-', ''),
              ...(params.workspaceSlug ? { workspaceSlug: params.workspaceSlug } : {}),
              ...(org ? { organizationId: org } : {}),
            },
            ...(org != null ? { workspaceId: org } : {}),
          }
          openBasecampTabFromUrl({
            openTabs,
            activeTabId,
            setOpenTabs,
            setActiveTabId,
            navigation,
            newTab,
            caller: 'url-basecamp-todo',
          })
        } else if (params.type === 'integration-basecamp-triage') {
          const newTabSlotId = generateTabSlotId();
          const newTab: OpenTab = {
            id: contentId,
            title: 'Weekly Triage',
            type: 'integration-basecamp-triage',
            isDirty: false,
            tabSlotId: newTabSlotId,
            integrationData: { integrationId: 'basecamp' },
            ...(defaultIntegrationWorkspaceId != null && { workspaceId: defaultIntegrationWorkspaceId }),
          };
          setOpenTabs([...openTabs, newTab]);
          setActiveTabId(contentId);
          navigation.navigate(newTabSlotId, { documentId: contentId, viewMode: 'single' }, "url-basecamp-triage");
        } else if (params.type === 'integration-voice-memos') {
          const newTabSlotId = generateTabSlotId();
          const newTab: OpenTab = {
            id: contentId,
            title: 'Voice Memos',
            type: 'integration-voice-memos',
            isDirty: false,
            tabSlotId: newTabSlotId,
            integrationData: {
              integrationId: params.integrationId || 'voice-memos'
            },
            ...(defaultIntegrationWorkspaceId != null && { workspaceId: defaultIntegrationWorkspaceId }),
          };
          setOpenTabs([...openTabs, newTab]);
          setActiveTabId(contentId);
          navigation.navigate(newTabSlotId, { documentId: contentId, viewMode: 'single' }, "url-voice-memos");
        } else if (params.type === 'integration-nextcloud') {
          const existingTab = openTabs.find((t) => t.type === 'integration-nextcloud')
          if (existingTab) {
            setActiveTabId(existingTab.id)
          } else {
            const newTabSlotId = generateTabSlotId()
            const newTab: OpenTab = {
              id: 'integration-nextcloud',
              title: 'Nextcloud',
              type: 'integration-nextcloud',
              isDirty: false,
              tabSlotId: newTabSlotId,
              integrationData: { integrationId: 'nextcloud' },
              ...(defaultIntegrationWorkspaceId != null && { workspaceId: defaultIntegrationWorkspaceId }),
            }
            setOpenTabs([...openTabs, newTab])
            setActiveTabId(newTab.id)
            navigation.navigate(newTabSlotId, nextcloudFieldsFromTab(newTab), 'url-nextcloud-browse')
          }
        } else if (params.type === 'integration-google-drive') {
          openSidebarMode('drive')
          const existingTab = openTabs.find((t) => t.type === 'integration-google-drive')
          if (existingTab) {
            setActiveTabId(existingTab.id)
          } else {
            const newTabSlotId = generateTabSlotId()
            const newTab: OpenTab = {
              id: contentId.startsWith('integration-google-drive') ? contentId : 'integration-google-drive',
              title: 'Google Drive',
              type: 'integration-google-drive',
              isDirty: false,
              tabSlotId: newTabSlotId,
              integrationData: { integrationId: 'google-drive' },
              ...(defaultIntegrationWorkspaceId != null && { workspaceId: defaultIntegrationWorkspaceId }),
            }
            setOpenTabs([...openTabs, newTab])
            setActiveTabId(newTab.id)
            navigation.navigate(newTabSlotId, { documentId: newTab.id, viewMode: 'list' }, 'url-drive-browse')
          }
        } else if (params.type === 'integration-google-drive-preview') {
          const fileId = params.driveFileId || contentId.replace(/^gdrive-preview-/, '')
          const newTabSlotId = generateTabSlotId()
          const newTab: OpenTab = {
            id: contentId.startsWith('gdrive-preview-') ? contentId : `gdrive-preview-${fileId}`,
            title: params.driveFileName || 'Google Drive file',
            type: 'integration-google-drive-preview',
            isDirty: false,
            tabSlotId: newTabSlotId,
            integrationData: {
              integrationId: 'google-drive',
              driveFileId: fileId,
              driveMimeType: params.driveMimeType || 'application/vnd.google-apps.document',
              ...(defaultIntegrationWorkspaceId != null
                ? { organizationId: defaultIntegrationWorkspaceId }
                : {}),
            },
            ...(defaultIntegrationWorkspaceId != null && { workspaceId: defaultIntegrationWorkspaceId }),
          }
          setOpenTabs([...openTabs, newTab])
          setActiveTabId(newTab.id)
          navigation.navigate(newTabSlotId, { documentId: newTab.id, viewMode: 'single' }, 'url-drive-preview')
        } else if (params.type === 'integration-product-board') {
          await ensureWorkspaceEnabledForUrlDocument(DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH)
          const productBoardWorkspaceId = getWorkspaceIdForPath?.(
            DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH
          )
          const existingTab = openTabs.find(t => t.type === 'integration-product-board');
          if (existingTab) {
            setActiveTabId(existingTab.id);
          } else {
            const newTabSlotId = generateTabSlotId();
            const newTab: OpenTab = {
              id: contentId,
              title: 'Product Board',
              type: 'integration-product-board',
              isDirty: false,
              tabSlotId: newTabSlotId,
              integrationData: { integrationId: 'product-board' },
              ...(productBoardWorkspaceId != null && { workspaceId: productBoardWorkspaceId }),
            };
            setOpenTabs([...openTabs, newTab]);
            setActiveTabId(contentId);
            navigation.navigate(newTabSlotId, { documentId: contentId, viewMode: 'single' }, "url-product-board");
          }
        }
      } catch (error) {
        console.error('[URL Change] Error loading content:', error);
        toast.error('Failed to load content', {
          description: 'The requested document or resource could not be found.'
        });
      }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setSelectedProjectId, navigation, createNewTab, switchToExistingTab, defaultIntegrationWorkspaceId, getWorkspaceIdForPath, workspaces, activeWorkspaceIds, ensureWorkspaceEnabledForUrlDocument, openExplorerAtFolder, workspacesRoute, hideOtherWorkspaces]);

  return {
    handleUrlChange,
  };
}
